/**
 * Flowcharts.tsx
 *
 * W26 — Visual Professionalism Overhaul
 * Replaces wrapping purple tile picker with a fixed top ledger bar.
 *
 * W19 — Checkpoint Flowcharts: canon labels, per-CP picker, pursuit/customer modes,
 * optional "where you are" banner when ?accountId=... is present.
 *
 * - CP names imported from helix-checkpoints.ts (single source of truth).
 * - Picker: single horizontal ledger bar — 10 cells, no wrapping, edge-to-edge.
 *   Active cell: border-bottom 3px solid #dc2626. Inactive: subtle hover.
 * - Mobile: dropdown fallback below 768px.
 * - Mode toggle: "New pursuit" vs "Existing customer".
 * - If ?accountId=... in URL, queries progression_snapshot for current_cp.
 *
 * Palette: gray #1a1f2e · red #dc2626 · white text · gray-400/gray-300 cell labels.
 * No emojis. No exclamation points. M-PACT hyphenated. Bryan K. Short.
 * Em-dash for CP{N} — {Name}. Tenant 1 only. CANON_VISUALS_LOCKED.
 */

import { useEffect, useState } from "react";
import { useSearchParams } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import { HELIX_CHECKPOINTS } from "@/lib/helix-checkpoints";
import { supabase } from "@/lib/session";
import { Download } from "lucide-react";
import AnswersTrailCard from "@/components/flowcharts/AnswersTrailCard";
import { ColorLegendFooter } from "@/components/ColorLegendFooter";

// ─── Component ────────────────────────────────────────────────────────────────

export default function Flowcharts() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive selected CP from URL param (default 1)
  const rawCp = searchParams.get("cp");
  const parsedCp = rawCp ? parseInt(rawCp, 10) : 1;
  const selectedCp = parsedCp >= 1 && parsedCp <= 10 ? parsedCp : 1;

  const [mode, setMode] = useState<"new" | "existing">("new");
  const [accountId, setAccountId] = useState<string | null>(null);
  const [accountName, setAccountName] = useState<string | null>(null);
  const [currentCp, setCurrentCp] = useState<number | null>(null);
  const [bannerLoading, setBannerLoading] = useState(false);
  const [existingImgError, setExistingImgError] = useState(false);

  // Select CP and sync to URL
  function selectCp(n: number) {
    const next = new URLSearchParams(searchParams.toString());
    next.set("cp", String(n));
    setSearchParams(next);
  }

  // Read accountId from URL on mount
  useEffect(() => {
    const aid = searchParams.get("accountId");
    if (aid) setAccountId(aid);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch progression_snapshot when accountId is present
  useEffect(() => {
    if (!accountId) return;
    setBannerLoading(true);

    async function fetchProgress() {
      try {
        const sb = supabase();

        const { data: acct } = await sb
          .from("accounts")
          .select("name")
          .eq("id", accountId)
          .eq("tenant_id", 1)
          .single();

        if (acct?.name) setAccountName(acct.name);

        const { data: snap } = await sb
          .from("progression_snapshot")
          .select("current_cp")
          .eq("account_id", accountId)
          .eq("tenant_id", 1)
          .order("snapshot_date", { ascending: false })
          .limit(1)
          .single();

        if (snap?.current_cp) {
          const n = parseInt(String(snap.current_cp).replace(/\D/g, ""), 10);
          if (n >= 1 && n <= 10) setCurrentCp(n);
        }
      } catch {
        // Banner is optional — silently omit on error
      } finally {
        setBannerLoading(false);
      }
    }

    fetchProgress();
  }, [accountId]);

  // Reset image error state when CP or mode changes
  useEffect(() => {
    setExistingImgError(false);
  }, [selectedCp, mode]);

  // Derived
  const activeCheckpoint = HELIX_CHECKPOINTS.find((cp) => cp.number === selectedCp)!;
  const svgSrc =
    mode === "new"
      ? `/flowcharts/cp${selectedCp}.svg`
      : `/flowcharts/cp${selectedCp}-existing.svg`;
  const bannerCpName = currentCp
    ? HELIX_CHECKPOINTS.find((cp) => cp.number === currentCp)?.name
    : null;

  return (
    <OperatorShell
      breadcrumbs={[{ label: "Home", href: "/" }, { label: "Checkpoint Flowcharts" }]}
      pageTitle="Checkpoint Flowcharts"
      pageMeta="Checkpoint canon · v4 · click to switch"
      pageActions={
        <a
          href="/flowcharts/all-checkpoints-v4.pdf"
          download
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background text-[#0B0B0F] text-[12px] font-medium px-3 py-1.5 hover:bg-[#F3F4F6] transition-colors whitespace-nowrap"
        >
          <Download className="h-3.5 w-3.5" aria-hidden />
          Download all (PDF)
        </a>
      }
    >
      <div className="max-w-5xl space-y-5">

        {/* ── Mode toggle ────────────────────────────────────────────────── */}
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-medium text-[#6B7280] uppercase tracking-wide mr-1">
            Mode
          </span>
          <button
            onClick={() => setMode("new")}
            style={{
              background: mode === "new" ? "#1F2937" : "#F9FAFB",
              color: mode === "new" ? "#FFFFFF" : "#374151",
              border: `1px solid ${mode === "new" ? "#1F2937" : "#E5E7EB"}`,
            }}
            className="rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors"
          >
            New pursuit
          </button>
          <button
            onClick={() => setMode("existing")}
            style={{
              background: mode === "existing" ? "#1F2937" : "#F9FAFB",
              color: mode === "existing" ? "#FFFFFF" : "#374151",
              border: `1px solid ${mode === "existing" ? "#1F2937" : "#E5E7EB"}`,
            }}
            className="rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors"
          >
            Existing customer
          </button>
        </div>

        {/* ── Ledger bar — desktop (hidden on mobile) ─────────────────────
             Single horizontal row of exactly 10 cells. No wrapping.
             Active cell: border-bottom 3px solid #dc2626.
             Inactive cell: subtle hover highlight.
        ─────────────────────────────────────────────────────────────────── */}
        <div
          className="hidden sm:flex"
          role="tablist"
          aria-label="Checkpoint picker"
          style={{
            width: "100%",
            minWidth: "100%",
            background: "#1a1f2e",
            borderRadius: "6px",
            overflow: "hidden",
          }}
        >
          {HELIX_CHECKPOINTS.map((cp) => {
            const isActive = cp.number === selectedCp;
            const isCurrentCp = currentCp === cp.number;
            return (
              <button
                key={cp.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => selectCp(cp.number)}
                title={`CP${cp.number} — ${cp.name}`}
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  borderBottom: isActive
                    ? "3px solid #dc2626"
                    : "3px solid transparent",
                  padding: "8px 4px 6px",
                  cursor: "pointer",
                  textAlign: "center",
                  transition: "background 0.12s",
                  position: "relative",
                  minWidth: 0,
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.06)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                  }
                }}
              >
                {/* "You are here" dot */}
                {isCurrentCp && (
                  <span
                    aria-label="You are here"
                    style={{
                      display: "block",
                      width: "5px",
                      height: "5px",
                      borderRadius: "50%",
                      background: "#dc2626",
                      position: "absolute",
                      top: "5px",
                      right: "5px",
                    }}
                  />
                )}
                {/* CP label — top line */}
                <span
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: 700,
                    color: isActive ? "#FFFFFF" : "#9CA3AF",
                    lineHeight: 1.2,
                    marginBottom: "2px",
                  }}
                >
                  CP{cp.number}
                </span>
                {/* CP name — bottom line, truncated */}
                <span
                  style={{
                    display: "block",
                    fontSize: "11px",
                    fontWeight: 400,
                    color: isActive ? "#E5E7EB" : "#6B7280",
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  }}
                >
                  {cp.name}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── Picker — mobile dropdown ───────────────────────────────────── */}
        <div className="sm:hidden">
          <label htmlFor="cp-select" className="sr-only">Select checkpoint</label>
          <select
            id="cp-select"
            value={selectedCp}
            onChange={(e) => selectCp(Number(e.target.value))}
            className="w-full rounded-md border border-border bg-background text-[13px] px-3 py-2 text-[#0B0B0F] focus:outline-none focus:ring-2 focus:ring-[#1F2937]"
          >
            {HELIX_CHECKPOINTS.map((cp) => (
              <option key={cp.id} value={cp.number}>
                CP{cp.number} · {cp.name}
                {currentCp === cp.number ? " — you are here" : ""}
              </option>
            ))}
          </select>
        </div>

        {/* ── "Where you are" banner ─────────────────────────────────────── */}
        {accountId && !bannerLoading && currentCp && bannerCpName && (
          <div
            className="rounded-md px-4 py-2.5 text-[13px] font-medium"
            style={{
              background: "#F0FDF4",
              border: "1px solid #86EFAC",
              color: "#166534",
            }}
          >
            You are at CP{currentCp} — {bannerCpName}
            {accountName ? ` on ${accountName}.` : "."}
          </div>
        )}

        {/* ── Active CP card ─────────────────────────────────────────────── */}
        <div
          key={`${selectedCp}-${mode}`}
          style={{
            border: "1px solid #2a2f3e",
            borderRadius: "8px",
            padding: "16px",
            background: "#FFFFFF",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          {/* Card header */}
          <div className="flex items-center justify-between mb-4">
            <h2
              style={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#FFFFFF",
                background: "#1a1f2e",
                display: "inline-block",
                padding: "4px 10px",
                borderRadius: "4px",
                margin: 0,
              }}
            >
              CP{activeCheckpoint.number} — {activeCheckpoint.name}
            </h2>
            <a
              href={`/flowcharts/cp${activeCheckpoint.number}-v4.pdf`}
              download
              className="inline-flex items-center gap-1 text-[12px] font-medium text-muted-foreground hover:text-[#DC2626] transition-colors"
              aria-label={`Download CP${activeCheckpoint.number} ${activeCheckpoint.name} flowchart PDF`}
            >
              <Download className="h-3 w-3" aria-hidden />
              Download PDF
            </a>
          </div>

          {/* SVG or placeholder */}
          <div className="overflow-x-auto">
            {mode === "new" ? (
              <img
                src={svgSrc}
                alt={`CP${activeCheckpoint.number} — ${activeCheckpoint.name} new pursuit flowchart, v4 canonical render`}
                loading="lazy"
                style={{ width: "100%", maxWidth: "960px", display: "block", margin: "0 auto" }}
              />
            ) : existingImgError ? (
              <div
                className="flex flex-col items-center justify-center rounded-md py-16 text-center"
                style={{ background: "#F9FAFB", border: "1px dashed #D1D5DB" }}
              >
                <p className="text-[14px] font-semibold text-[#374151]">
                  CP{activeCheckpoint.number} — {activeCheckpoint.name}
                </p>
                <p className="mt-1.5 text-[13px] text-[#9CA3AF]">
                  Existing customer flow — coming soon
                </p>
              </div>
            ) : (
              <img
                src={svgSrc}
                alt={`CP${activeCheckpoint.number} — ${activeCheckpoint.name} existing customer flowchart, v4 canonical render`}
                loading="lazy"
                style={{ width: "100%", maxWidth: "960px", display: "block", margin: "0 auto" }}
                onError={() => setExistingImgError(true)}
              />
            )}
          </div>
        </div>

        {/* ── Answers trail card ─────────────────────────────────────── */}
        {accountId ? (
          <AnswersTrailCard
            accountId={accountId}
            accountName={accountName}
            selectedCp={selectedCp}
          />
        ) : (
          <div
            className="rounded-lg border px-5 py-4"
            style={{ borderColor: "#E5E7EB", background: "#FAFAF9" }}
          >
            <p className="text-[13px] text-[#6B7280]">
              Select an account to see the questions and answers locked at this checkpoint.
            </p>
          </div>
        )}

        {/* T2 Item 4 — Color Legend Footer (Canon §19) */}
        <ColorLegendFooter />

      </div>
    </OperatorShell>
  );
}
